import { useMemo } from 'react'
import { countries as configured } from '../config/travel'

/**
 * The countries shown on /travel, straight from src/config/travel.js.
 *
 * Blog posts used to drive this list, which meant a country only appeared once
 * it had been written up. The config is the source of truth instead, so the map
 * covers everywhere visited rather than everywhere blogged about.
 *
 * Sorted by name here rather than trusting the file's own order, so an entry
 * appended in the wrong place still lands correctly in the rail.
 */
export const useTravelCountries = () => {
  const countries = useMemo(
    () =>
      [...configured]
        .map((country) => ({ ...country, key: country.code }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    []
  )

  /**
   * Where to park the globe so the most markers are on the near face. Plain
   * mean longitude would land in the ocean for a spread like Canada + Asia, so
   * average the points as unit vectors and take the bearing back.
   */
  const overviewCenter = useMemo(() => {
    if (!countries.length) return [10, 20]
    const toRad = Math.PI / 180
    let x = 0
    let y = 0
    for (const country of countries) {
      x += Math.cos(country.lng * toRad)
      y += Math.sin(country.lng * toRad)
    }
    const lng = Math.atan2(y / countries.length, x / countries.length) / toRad
    const lat = countries.reduce((sum, c) => sum + c.lat, 0) / countries.length
    return [lng, Math.max(-50, Math.min(60, lat))]
  }, [countries])

  return { countries, overviewCenter }
}
